import { useEffect, useRef, useState } from "react";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel } from "@mui/material";
import { useAuth } from "@/services/contexts/auth.context";
import { useRouter } from "next/router";
import { useCardsAPI, useCardsState } from "@/services/contexts/cards.context";
import { useFavourites, useGetMyPlaylists } from "@/services/api/playlist/queries";
import {
  useAddToFavorites,
  useAddToPlaylist,
  useCreatePlaylist,
  useRemoveFromFavorites,
} from "@/services/api/playlist/mutations";
import { useMapAPI } from "@/services/contexts/map.context";

interface CheckboxEvent {
  target: {
    checked: boolean;
    value: string;
  };
}

function AddToPlaylistModal() {
  const api = useCardsAPI();
  const state = useCardsState();
  const mapAPI = useMapAPI();
  const open = state.playlistDialog;
  const eventID = state.eventID;

  const { data: data, mutate: mutatePlaylists } = useGetMyPlaylists(!open);
  const { data: favData, mutate: mutateFav } = useFavourites(!open);
  const { trigger: createPlaylist } = useCreatePlaylist();
  const { trigger: addToPlaylist } = useAddToPlaylist();
  const { trigger: addToFavorites } = useAddToFavorites();
  const { trigger: removeFromFavorites } = useRemoveFromFavorites();
  const [show, setShow] = useState({ Create: "flex", Input: "none" });
  const [input, setInput] = useState("");
  const [playlistIDs, setPlaylistIDs] = useState<number[]>([]);
  const [favourite, setFavourite] = useState(false);
  const { isAuthenticated } = useAuth();
  const router = useRouter();
  const inputReference = useRef<HTMLInputElement>(null);
  const playlists = data?.result;

  useEffect(() => {
    if (show.Input === "flex") {
      inputReference.current?.focus();
    }
  }, [show]);

  useEffect(() => {
    let playlistsChecked: number[] = [];

    if (!eventID) return;

    if (favData?.result) {
      const isFav = favData.result.some((item: any) => item.event_id === parseInt(eventID));
      setFavourite(isFav);
    }

    if (playlists) {
      playlists.forEach(list => {
        const playlistListingIds = Array.from(new Set(list.playlist_content.map(p => p.event_id)));
        const playlistChecked = playlistListingIds.includes(parseInt(eventID));

        if (playlistChecked) {
          playlistsChecked = [...playlistsChecked, list.ID];
        }
      });

      setPlaylistIDs(playlistsChecked);
    }

    if (!isAuthenticated) {
      router.push({ pathname: "/signup" }).then();
    }
  }, [data, eventID]);

  const addList = async () => {
    try {
      const addListResponse = await createPlaylist({
        playlist_name: input,
      });

      await mutatePlaylists();

      setPlaylistIDs(prevState => [...prevState, addListResponse.result.ID]);
      setInput("");
      setShow({
        Create: "flex",
        Input: "none",
      });

      return addListResponse;
    } catch (error) {
      console.error("Error adding list:", error);
    }
  };

  const openInput = () => {
    setShow({
      Create: "none",
      Input: "flex",
    });
  };

  const closeAndReset = () => {
    setInput("");
    api.setPlaylistDialog(false);
    api.onIconStateChange(!state.icon);
  };

  const handleCheckbox = (e: CheckboxEvent, favCheckbox = false) => {
    const target = e.target;
    const value = parseInt(target.value);
    const checked = target.checked;

    if (checked) {
      if (favCheckbox) {
        setFavourite(true);
      } else {
        setPlaylistIDs(prevState => [...prevState, value]);
      }
    } else {
      if (favCheckbox) {
        setFavourite(false);
      } else {
        setPlaylistIDs(prevState => prevState.filter(id => id !== value));
      }
    }
  };

  const addToPlaylists = async () => {
    let playlists = playlistIDs;
    if (input) {
      const addListResponse = await addList();

      if (addListResponse?.result?.ID) {
        playlists = [...playlists, addListResponse.result.ID];
      }
    }
    if (favourite) {
      await addToFavorites({
        event_id: parseInt(eventID),
      });
    } else {
      await removeFromFavorites({
        event_id: parseInt(eventID),
      });
    }

    await addToPlaylist({
      event_id: parseInt(eventID),
      playlist_ids: playlists,
    });

    await mutateFav();
    await mutatePlaylists();
    await mapAPI.mutateEvents();

    api.setPlaylistDialog(false);
    api.onIconStateChange(!state.icon);
  };

  return (
    <Dialog open={open} onClose={closeAndReset} fullWidth={true} maxWidth={"xs"} className="rounded-2xl">
      <DialogTitle className="flex items-center justify-between text-2xl border-b border-gray-300 pb-0 mb-4">
        <div>Add to...</div>
        <IconButton aria-label="close" onClick={closeAndReset} className="text-gray-500">
          <CloseIcon className="w-8 h-8 fill-gray-300" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <div className="overflow-visible flex flex-col justify-start items-start ml-5 pt-2">
          <FormControlLabel
            className="mb-3 overflow-visible"
            control={
              <Checkbox
                size="small"
                className="mr-2 p-0 overflow-visible"
                checked={favourite}
                onChange={e => handleCheckbox(e as unknown as CheckboxEvent, true)}
              />
            }
            label={<span className="text-base ml-1 font-bold">My Favourites</span>}
          />
          {playlists &&
            playlists.map(list => {
              return (
                <FormControlLabel
                  key={list.ID}
                  itemID={list.ID.toString()}
                  className="mb-3 overflow-visible pt-1 pb-1"
                  control={
                    <Checkbox
                      value={list.ID.toString()}
                      checked={playlistIDs.includes(list.ID)}
                      onChange={e => handleCheckbox(e as unknown as CheckboxEvent, false)}
                      size="small"
                      className="mr-2 p-0 overflow-visible"
                    />
                  }
                  label={<span className="text-base ml-1">{list.name}</span>}
                />
              );
            })}
        </div>
        <div
          onClick={openInput}
          className={`border border-gray-300 text-left flex items-center bg-white text-base h-12 pl-2 pr-1 rounded-lg hover:bg-gray-200 cursor-pointer`}
          style={{
            display: show.Create,
          }}
        >
          <AddIcon className="scale-75" /> Create playlist
        </div>
        <div
          className={`bg-white flex items-center ml-1 pl-1`}
          style={{
            display: show.Input,
          }}
        >
          <Checkbox
            size="small"
            className="mr-2 p-0 overflow-visible"
            checked={true}
            disabled={true}
            onChange={e => handleCheckbox(e as unknown as CheckboxEvent, true)}
          />
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            ref={inputReference}
            className="flex-1 border-none border-b border-gray-300 p-1 ml-1 mr-1 text-base focus:outline-hidden"
          />
        </div>
      </DialogContent>
      <DialogActions>
        <button type={"button"} className={"btn-pill bg-pink-happyway"} onClick={addToPlaylists}>
          Save
        </button>
      </DialogActions>
    </Dialog>
  );
}

export default AddToPlaylistModal;
